import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useWebPubSub } from "~/features/projectTop/webPubSubContext";
import { GenerateApi } from "~/functions/api/generate";
import type { RootState } from "~/store";
import { connect, createClient, disconnect } from "~/utils/webPubSubUtils";

export const useWebPubSubConnection = () => {
  const { organization, currentUser } = useSelector((state: RootState) => state.auth);
  const { client, setClient } = useWebPubSub();
  const lastOrgIdRef = useRef<string | null>(null);
  const isConnectingRef = useRef(false);

  useEffect(() => {
    const setup = async () => {
      if (!organization?.id || !currentUser?.organization_id) return;
      if (client && lastOrgIdRef.current === organization.id) return;
      if (isConnectingRef.current) return;

      if (client && lastOrgIdRef.current && lastOrgIdRef.current !== organization.id) {
        await disconnect(client);
        setClient(null);
      }

      if (!client) {
        isConnectingRef.current = true;
        try {
          const generateApi = new GenerateApi();
          const response = await generateApi.getClientAccessUrl(currentUser.organization_id);
          if (response !== null) {
            const webPubSubClient = await createClient(response);
            if (webPubSubClient !== null) {
              await connect(webPubSubClient);
              setClient(webPubSubClient);
            }
            lastOrgIdRef.current = organization.id;
          }
        } finally {
          isConnectingRef.current = false;
        }
      }
    };
    setup();
  }, [organization?.id, currentUser?.organization_id, client, setClient]);

  return { client, isConnected: !!client };
};
